const PLUGIN_NAME = 'cursor'
const MODEL_KEY = '$cursor';
const EVENT_TYPE_CURSOR = `$chartjs.${PLUGIN_NAME}.cursor_event:`;
const EVENT_TYPE_VERTICAL_AXIS = `$chartjs.${PLUGIN_NAME}.vertical_axis_event:`;

const defaultOptions = {
    background: {
        enabled: false,
        color: 'whiteSmoke',
    },
    line: {
        color: '#F66',
        width: 1,
        dashPattern: [5, 5],
    },
    sync: {
        enabled: true,
        group: 1,
        alignVerticalAxes: true,
    },
    snap: {
        enabled: false,
    },
};

/**
 * Returns value at object 'address' or undefined
 * For example let _root_ be * {'a':[], b:{ {'b1':false, 'b2':{'name':"Dave"}, 'b3':undefined}, c:0}}
 * 1) let _path_ be 'a.b.b2.name' then result shall be "Dave".
 * 2) let _path_ be 'a.b3.missing.name' then result shall be undefined.
 * @param root object to navigate using path, ex. {'a': {'b':{ 'c':"x"}}}
 * @param path string representation of a path to the desired value, ex.: 'a.b.c' => 'x'
 * @returns {undefined|*}
 */
function getValueAtPath(root, path) {
    if (typeof path !== 'string') return undefined;
    const components = path.split('.');
    let value = root;
    while (components.length > 0) {
        value = value[components.shift()];
        if (value === undefined) return undefined;
    }
    return value;
}

function _option(chart, path) {
    const result = getValueAtPath(chart.options.plugins[PLUGIN_NAME], path);
    return result !== undefined ? result : getValueAtPath(defaultOptions, path);
}

function _x(chart) {
    return chart._active.length && _option(chart, 'snap.enabled')
        ? chart._active[0].element.x
        : chart[MODEL_KEY].x;
}

function _xScale(chart) {
    return chart.data.datasets.length
        ? chart.scales[chart.getDatasetMeta(0).xAxisID]
        : null;
}

function _yScale(chart) {
    return chart.data.datasets.length ? chart.scales[chart.getDatasetMeta(0).yAxisID] : null;
}

function _handleSyncEvent(chart, event) {
    const detail = event.detail as CursorPayload;
    // stop if the sync event was fired from this chart
    if (detail.chartId === chart.id) {
        return;
    }

    // stop if the sync event was fired from a different group
    if (detail.syncGroup !== _option(chart, 'sync.group')) {
        return;
    }

    const xScale = _xScale(chart);
    if (!xScale) {
        return;
    }

    // Safari fix
    let buttons = (detail.original.native.buttons === undefined ? detail.original.native.which : detail.original.native.buttons);
    if (detail.original.type === 'mouseup') {
        buttons = 0;
    }

    chart[MODEL_KEY].enabled = detail.inChartArea;
    chart._eventHandler({
        // do not transmit click events to prevent unwanted changing of synced charts.
        type: detail.original.type === "click" ? "mousemove" : detail.original.type,
        chart: chart,
        x: xScale.getPixelForValue(detail.xValue),
        y: detail.original.y,
        native: {
            buttons: buttons
        },
        stop: true
    });
}

function _handleVerticalAxisEvent(chart, event) {
    const detail = event.detail as VerticalAxisPayload;

    // stop if the sync event was fired from this chart
    if (detail.chartId === chart.id) {
        return;
    }
    // stop if the minYPadding isn't changing
    if (chart[MODEL_KEY].minYPadding === detail.chartAreaLeft) {
        return;
    }
    chart[MODEL_KEY].minYPadding = detail.chartAreaLeft;
}

function _drawCursorLine(chart) {
    const x = _x(chart);
    if (x === null) {
        return;
    }

    const yScale = _yScale(chart);
    const yStart = yScale.getPixelForValue(yScale.max);
    const yEnd = yScale.getPixelForValue(yScale.min);

    const ctx = chart.ctx;
    ctx.beginPath();
    ctx.setLineDash(_option(chart, 'line.dashPattern'));
    ctx.moveTo(x, yStart);
    ctx.lineWidth = _option(chart, 'line.width');
    ctx.strokeStyle = _option(chart, 'line.color');
    ctx.lineTo(x, yEnd);
    ctx.stroke();
}

function _drawBackground(chart) {
    const ctx = chart.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = _option(chart, 'background.color');
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
}

class CursorPayload {
    chartId: string;
    syncGroup: number;
    original: any;
    inChartArea: boolean;
    chartAreaLeft: number;
    xValue: number;

    constructor(
        chartId: string,
        syncGroupId: number,
        originalEvent: Event,
        inChartArea: boolean,
        chartAreaLeft: number,
        xValue: number
    ) {
        this.chartId = chartId;
        this.syncGroup = syncGroupId;
        this.original = originalEvent;
        this.inChartArea = inChartArea;
        this.chartAreaLeft = chartAreaLeft;
        this.xValue = xValue;
    }
}

class VerticalAxisPayload {
    chartId: string;
    chartAreaLeft: number;

    constructor(chartId: string, chartAreaLeft: number,
    ) {
        this.chartId = chartId;
        this.chartAreaLeft = chartAreaLeft;
    }
}

// noinspection JSUnusedGlobalSymbols
export default {

    id: PLUGIN_NAME,

    start: function (chart) {
        if (chart.options.plugins[PLUGIN_NAME] === undefined) {
            chart.options.plugins[PLUGIN_NAME] = defaultOptions;
        }

        chart[MODEL_KEY] = {
            enabled: false,
            x: null,
            syncEventHandler() {
            },
            verticalAxisEventHandler() {
            },
            minYPadding: 0,
            reset: function () {
            }.bind(this)
        };
        // Init cursor event handler
        if (_option(chart, 'sync.enabled')) {
            chart[MODEL_KEY].syncEventHandler = function (event) {
                _handleSyncEvent(chart, event);
            }.bind(this);
            window.addEventListener(EVENT_TYPE_CURSOR, chart[MODEL_KEY].syncEventHandler);
        }
        // Init vertical axis event handler
        if (_option(chart, 'sync.alignVerticalAxes')) {
            chart[MODEL_KEY].verticalAxisEventHandler = function (event) {
                _handleVerticalAxisEvent(chart, event);
            }.bind(this);
            window.addEventListener(EVENT_TYPE_VERTICAL_AXIS, chart[MODEL_KEY].verticalAxisEventHandler);
        }
    },

    destroy: function (chart) {
        if (_option(chart, 'sync.enabled')) {
            window.removeEventListener(EVENT_TYPE_CURSOR, chart[MODEL_KEY].syncEventHandler);
        }
        if (_option(chart, 'sync.alignVerticalAxes')) {
            window.removeEventListener(EVENT_TYPE_VERTICAL_AXIS, chart[MODEL_KEY].verticalAxisEventHandler);
        }
    },

    afterEvent: function (chart, args, options) {
        const xScale = _xScale(chart);
        if (!xScale) {
            return;
        }

        const event = args.event
        // fire event for all other linked charts
        if (_option(chart, 'sync.enabled') && !event.stop) {
            const newEvent = new CustomEvent<CursorPayload>(EVENT_TYPE_CURSOR, {
                detail: new CursorPayload(
                    chart.id,
                    _option(chart, 'sync.group'),
                    event,
                    args.inChartArea,
                    chart.chartArea.left,
                    xScale.getValueForPixel(event.x),
                )
            });
            window.dispatchEvent(newEvent);
        }

        chart[MODEL_KEY].enabled = args.inChartArea;

        if (!chart[MODEL_KEY].enabled) {
            if (event.x > xScale.getPixelForValue(xScale.max)) {
                chart.update('none');
            }
            return false;
        }
        chart[MODEL_KEY].x = event.x;
    },

    beforeDraw: (chart) => {
        if (_option(chart, 'background.enabled') && _option(chart, 'background.color')) {
            _drawBackground(chart);
        }
    },

    afterLayout(chart, args, options) {
        const leftAfterLayout = getValueAtPath(chart, 'chartArea.left');

        if (leftAfterLayout === undefined) {
            return;
        }

        if (leftAfterLayout !== chart[MODEL_KEY].minYPadding) {
            // Vertical Chart has moved, emit event
            const newEvent = new CustomEvent<VerticalAxisPayload>(EVENT_TYPE_VERTICAL_AXIS, {
                detail: new VerticalAxisPayload(
                    chart.id,
                    leftAfterLayout,
                )
            });
            window.dispatchEvent(newEvent);
        }
    },

    beforeBuildTicks(chart, {scale}, options) {
        if (_option(chart, 'sync.alignVerticalAxes')
            && scale.id === 'y'
        ) {
            scale.paddingLeft = 0;
        }
    },

    afterBuildTicks(chart, {scale}, options) {
        if (_option(chart, 'sync.alignVerticalAxes')
            && scale.id === 'y'
        ) {
            scale.paddingLeft = Math.max(chart[MODEL_KEY].minYPadding, chart.scales.y.paddingLeft);
        }
    },

    afterDraw(chart, args, options) {
        if (chart[MODEL_KEY].enabled) {
            _drawCursorLine(chart);
        }
    }
};
